import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';

import { Transition } from '@headlessui/react';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';

import { ProductType } from '~/interfaces/product';

interface ThankYouMessageProps {
  show: boolean;
  onClose: () => void;
  onContinue: () => void;
  isFree?: boolean;
  productType?: ProductType;
}

export default function ThankYouMessage({ show, onClose, onContinue, isFree, productType }: ThankYouMessageProps) {
  const { t } = useTranslation();

  const title = (() => {
    if (!isFree) return t('products.purchase_success_title');
    if (productType === ProductType.WEBINAR) return t('products.webinar_registration_success_title');
    if (productType === ProductType.COACHING_SESSION) return t('products.free_coaching_success_title');
    return t('products.free_content_success_title');
  })();

  const message = (() => {
    if (!isFree) return t('products.purchase_success_message');
    if (productType === ProductType.WEBINAR) return t('products.webinar_registration_success_message');
    if (productType === ProductType.COACHING_SESSION) return t('products.free_coaching_success_message');
    return t('products.free_content_success_message');
  })();

  return (
    <Transition
      appear
      show={show}
      as={Fragment}
      enter="transition-opacity duration-300 ease-out"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-200 ease-in"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="max-w-md w-full mx-4 p-6 relative bg-wt-background text-wt-text-on-background border border-wt-text-on-background/20 rounded-wt shadow-xl">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 rounded-full p-2 text-wt-text-on-background hover:bg-wt-text-on-background hover:bg-opacity-10"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Close</span>
          </button>

          <div className="text-center">
            <div
              className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4"
              style={{ backgroundColor: 'color-mix(in srgb, var(--wt-primary-color) 20%, transparent)' }}
            >
              <CheckCircleIcon className="h-8 w-8" aria-hidden="true" style={{ color: 'var(--wt-primary-color)' }} />
            </div>

            <h3 className="text-lg font-medium mb-2 text-wt-text-on-background wt-header-font">{title}</h3>

            <p className="text-sm mb-6 text-wt-text-on-background/90 wt-body-font">{message}</p>

            <button
              type="button"
              onClick={onContinue}
              className="w-full inline-flex justify-center border border-wt-primary px-4 py-2 text-base font-medium shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-wt-primary focus:ring-offset-2 transition-opacity bg-wt-primary text-wt-text-on-primary rounded-wt wt-button-font"
            >
              {t('shared.cta.continue')}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  );
}
